import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Linking,
  Platform,
  SafeAreaView,
  ScrollView,
  View,
} from 'react-native';
import { Text } from '@/components/Text';
import api from '../../api/axios';
import CustomCheckBox from '@/components/SharedComponents/CustomCheckBox/CustomCheckBox';
import colors from '@/config/colors';
import { isPatientApp } from '@/config/constants';
import SignUpMessages from './SignUpMessages';
import { styles } from './styles';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Links } from '@/config/links';

export function SignUpController() {
  const {
    control,
    formState: { errors, isSubmitted },
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      handleSubmission: false,
    },
  });

  const isAndroid = Platform.OS === 'android';

  const titleText = isPatientApp
    ? 'Registrieren Sie sich hier für neolexon Aphasie als Patient:in'
    : 'Registrieren Sie sich hier als Therapeut:in bei neolexon';

  const dataProtectionText = isPatientApp
    ? 'Ich willige ein, dass meine personenbezogenen Daten, darunter auch Gesundheitsdaten, durch neolexon verarbeitet werden, um eine Sprachtherapie durchzuführen. Weitere Informationen zu meinen Rechten, der Speicherung und Verarbeitung der Daten finde ich in der Datenschutzerklärung. Ich bestätige diese gelesen zu haben. Ich kann meine Einwilligung jederzeit widerrufen.'
    : 'Ich willige ein, dass meine personenbezogenen Daten durch neolexon verarbeitet werden, um eine Sprachtherapie durchzuführen. Weitere Informationen zu meinen Rechten, der Speicherung und Verarbeitung der Daten finde ich in der Datenschutzerklärung. Ich bestätige diese gelesen zu haben. Ich kann meine Einwilligung jederzeit widerrufen.';

  const email = useRef();
  const filledPassword = watch('password', '');

  const [isUseOfDataConfirmed, setUseOfDataConfirmation] = useState(false);
  const [isDataStudyConsent, setDataStudyConsent] = useState(false);
  const [isNewsletterConfirmed, setNewsletterConfirmation] = useState(false);

  const [isModalVisible, setModalVisible] = useState(false);
  const [isModalErrorVisible, setModalErrorVisible] = useState(false);

  const [setNewsletter] = useState(false);
  const showNewsletter = isPatientApp ? setNewsletter : !setNewsletter;

  const [setSubtitle] = useState(false);
  const showSubtitle = isPatientApp ? setSubtitle : !setSubtitle;
  const errorMessageCondition =
    isSubmitted && (Object.keys(errors).length !== 0 || !isUseOfDataConfirmed);

  const InputStyles = {
    inputContainerStyle: {
      borderBottomWidth: 0,
      justifyContent: 'center',
      paddingHorizontal: 0,
      marginHorizontal: 0,
      height: '100%',
    },
    therapistInputWrapper: {
      width: '75%',
      alignSelf: 'center',
    },
  };

  const onSignInPressed = async (data) => {
    if (Object.keys(errors).length === 0) {
      if (isUseOfDataConfirmed) {
        const credentials = {};
        credentials.firstName = data.firstName;
        credentials.lastName = data.lastName;
        credentials.email = data.email.trim();
        credentials.password = data.password;
        credentials.dataProcessingAgreement = isUseOfDataConfirmed;
        credentials.studyConsent = isDataStudyConsent;

        if (isPatientApp) {
          credentials.roles = ['client'];
          credentials.digaUser = true;
          credentials.registrationChannel = 'neolexon-aphasie';
        } else {
          credentials.roles = ['user'];
          credentials.newsletter = isNewsletterConfirmed;
          credentials.registrationChannel = 'neolexon-therapeut';
        }

        api
          .post('/api/auth/signup', credentials, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then((response) => {
            email.current = credentials.email;
            setValue('email', data.email);
            setModalVisible(true);
            return response;
          })
          .catch(() => {
            setModalErrorVisible(true);
          });
      }
    }
  };
  const renderWrappedField = useCallback(
    (props) => {
      const patientProps = isPatientApp
        ? {
            containerStyle: styles.inputText,
            inputContainerStyle: InputStyles.inputContainerStyle,
            errorMessageStyle: styles.errorMessage,
          }
        : {};

      return isPatientApp ? (
        <SharedFormField {...props} {...patientProps} />
      ) : (
        <View style={InputStyles.therapistInputWrapper}>
          <SharedFormField {...props} />
        </View>
      );
    },
    [isPatientApp],
  );
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <KeyboardAwareScrollView enableOnAndroid>
          <View style={styles.container}>
            <Text style={styles.title}>{titleText}</Text>
            <Text style={[styles.subtitle]}>
              Und testen Sie 14 Tage kostenlos. Danach entstehen keine
              automatischen Kosten.
            </Text>
            {!showSubtitle && (
              <Text style={[styles.subtitle, { color: colors.red }]}>
                Wichtig: Sie benötigen eine:n Therapeut:in, um unsere App nutzen
                zu können.
              </Text>
            )}
            <View style={styles.formWrapper}>
              <View style={styles.formField}>
                {renderWrappedField({
                  control,
                  name: 'firstName',
                  label: 'Vorname',
                  rules: { required: 'Vorname wird benötigt.' },
                  autoCapitalize: 'words',
                })}
              </View>
              <View style={styles.formField}>
                {renderWrappedField({
                  control,
                  name: 'lastName',
                  label: 'Nachname',
                  rules: { required: 'Nachname wird benötigt.' },
                  autoCapitalize: 'words',
                })}
              </View>
              <View style={styles.formField}>
                {renderWrappedField({
                  control,
                  name: 'email',
                  label: 'E-Mail-Adresse',
                  keyboardType: 'email-address',
                  rules: {
                    required: 'E-Mail-Adresse wird benötigt.',
                    pattern: {
                      value:
                        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi,
                      message: 'E-Mail-Adresse ungültig',
                    },
                  },
                })}
              </View>
              <View style={styles.formField}>
                {renderWrappedField({
                  control,
                  name: 'password',
                  label: 'Passwort (mindestens 8 Zeichen)',
                  secureTextEntry: true,
                  rules: {
                    required: 'Passwort wird benötigt.',
                    minLength: {
                      value: 8,
                      message:
                        'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                    },
                    validate: (value) => passwordValidate(value),
                  },
                })}
              </View>

              <View style={styles.formField}>
                {renderWrappedField({
                  control,
                  name: 'confirmPassword',
                  label: 'Wiederholung Passwort',
                  secureTextEntry: true,
                  rules: {
                    required: 'Passwortwiederholung wird benötigt.',
                    validate: (value) =>
                      confirmPasswordValidate(value, filledPassword),
                  },
                })}
              </View>
            </View>
            {/*Finish form wrapper*/}

            <View style={styles.checkboxWrapper}>
              <View style={styles.checkbox}>
                <CustomCheckBox
                  onChecked={() =>
                    setUseOfDataConfirmation(!isUseOfDataConfirmed)
                  }
                  checked={isUseOfDataConfirmed}
                  isLabelComponent={isAndroid}
                  label={
                    <>
                      <PressableNeolexon
                        onPress={() =>
                          setUseOfDataConfirmation(!isUseOfDataConfirmed)
                        }
                      >
                        <Text style={styles.checkBoxText}>
                          {dataProtectionText}{' '}
                          {!isUseOfDataConfirmed && (
                            <Text
                              style={[
                                isSubmitted
                                  ? styles.mandatoryErrorCheck
                                  : styles.mandatoryTextCheck,
                              ]}
                            >
                              (Pflichtfeld)
                            </Text>
                          )}
                        </Text>
                        <PressableNeolexon
                          onPress={() =>
                            Linking.openURL(Links.datenschutz.href)
                          }
                        >
                          <Text style={styles.openLink}>
                            Datenschutzerklärung öffnen{' '}
                          </Text>
                        </PressableNeolexon>
                      </PressableNeolexon>
                    </>
                  }
                />
              </View>

              <View style={styles.checkbox}>
                <CustomCheckBox
                  checked={isDataStudyConsent}
                  onChecked={() => {
                    setDataStudyConsent(!isDataStudyConsent);
                  }}
                  isLabelComponent={isAndroid}
                  label={
                    <View>
                      <PressableNeolexon
                        onPress={() => setDataStudyConsent(!isDataStudyConsent)}
                      >
                        <Text style={styles.checkBoxText}>
                          Ich willige ein, dass meine Nutzungsdaten anonymisiert
                          durch neolexon verarbeitet werden, um die dauerhafte,
                          technische Funktionsfähigkeit und Nutzerfreundlichkeit
                          der neolexon Apps zu gewährleisten sowie diese stetig
                          weiterzuentwickeln. Ich kann meine Einwilligung
                          jederzeit widerrufen. Diese Einwilligung ist
                          <Text style={styles.boldMessage}> optional</Text>.
                        </Text>
                      </PressableNeolexon>
                    </View>
                  }
                />
              </View>

              <View style={styles.checkbox}>
                {showNewsletter ? (
                  <CustomCheckBox
                    checked={isNewsletterConfirmed}
                    onChecked={() =>
                      setNewsletterConfirmation(!isNewsletterConfirmed)
                    }
                    isLabelComponent={isAndroid}
                    label={
                      <View>
                        <PressableNeolexon
                          onPress={() =>
                            setNewsletterConfirmation(!isNewsletterConfirmed)
                          }
                        >
                          <Text style={styles.checkBoxText}>
                            Zum Newsletter anmelden, um über Neuigkeiten auf dem
                            Laufenden zu bleiben. Ich kann meine Einwilligung
                            jederzeit widerrufen. Diese Einwilligung ist
                            <Text style={styles.boldMessage}> optional</Text>.
                          </Text>
                        </PressableNeolexon>
                      </View>
                    }
                  />
                ) : null}
              </View>
            </View>

            <View>
              {!isPatientApp && (
                <View style={styles.subtitle2Container}>
                  <Text style={styles.subtitle2}>
                    Bitte beachten Sie, dass Sie die von uns bereitgestellen{' '}
                    <Text
                      onPress={() => Linking.openURL(Links.datenschutz.href)}
                      style={styles.openLink}
                    >
                      {' '}
                      Datenschutzdokumente{' '}
                    </Text>{' '}
                    ausfüllen sollten.
                  </Text>
                </View>
              )}
              <View style={styles.subtitle2Container}>
                <Text style={styles.subtitle2}>
                  Mit Ihrer Registrierung stimmen Sie den{'  '}
                  <Text
                    onPress={() => Linking.openURL(Links.agb.href)}
                    style={[styles.openLink, styles.font20]}
                  >
                    {Links.agb.label}
                  </Text>
                  {'  '}
                  zu.{'\n'}
                </Text>
              </View>
            </View>

            <View>
              <PressableNeolexon
                onPress={handleSubmit(onSignInPressed)}
                style={styles.registrationButton}
              >
                <Text style={styles.registration}> Registrieren </Text>
              </PressableNeolexon>
              {!!errorMessageCondition && (
                <Text style={styles.registrationErrorMessage}>
                  Bitte überprüfen Sie Ihre Eingaben und versuchen es erneut.
                </Text>
              )}
            </View>

            <SignUpMessages
              isModalVisible={isModalVisible}
              setModalVisible={setModalVisible}
              isModalErrorVisible={isModalErrorVisible}
              setModalErrorVisible={setModalErrorVisible}
              email={email.current}
            />
          </View>
        </KeyboardAwareScrollView>
      </ScrollView>
    </SafeAreaView>
  );
}
